import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Menu, Plus, Radar, RefreshCw, Search, SlidersHorizontal, Trophy, X } from "lucide-react";
import { useAdminLayout } from "../AdminLayoutContext";

export default function AdminLeaguesChrome({
  deskMode = "managed",
  onDeskModeChange,
  search,
  onSearchChange,
  loading,
  onRefresh,
  onAdd,
  onDiscover,
  discovering = false,
  filtersOpen,
  onFiltersToggle,
  activeFilterCount,
  summary,
  candidateSummary,
  totalLoaded,
}) {
  const { openMobileMenu } = useAdminLayout();
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  return (
    <>
      <header className="am-chrome">
        <div className="am-chrome-row">
          <div className="am-chrome-lead">
            <button
              type="button"
              className="am-chrome-btn am-chrome-btn-icon lg:hidden"
              onClick={openMobileMenu}
              aria-label="Open menu"
            >
              <Menu size={16} strokeWidth={2.2} />
            </button>

            <div className="am-chrome-brand" aria-label="Leagues Desk">
              <Trophy size={12} strokeWidth={2.3} aria-hidden />
              <span>Leagues Desk</span>
            </div>

            <div className="am-chrome-stats" aria-label="Leagues summary">
              <span className="am-chrome-stat tabular-nums">
                <em>{totalLoaded ?? "—"}</em> {deskMode === "candidates" ? "candidates" : "leagues"}
              </span>
              {deskMode === "managed" ? (
                <span className="am-chrome-stat am-chrome-stat-live tabular-nums">
                  <em>{summary?.enabled ?? 0}</em> active
                </span>
              ) : (
                <span className="am-chrome-stat tabular-nums">
                  <em>{candidateSummary?.total ?? totalLoaded ?? 0}</em> in pool
                </span>
              )}
            </div>
          </div>

          <div className="am-chrome-trail">
            <div className="alg-desk-mode-switch" role="tablist" aria-label="Leagues desk mode">
              <button
                type="button"
                role="tab"
                aria-selected={deskMode === "managed"}
                className={deskMode === "managed" ? "is-on" : ""}
                onClick={() => onDeskModeChange?.("managed")}
              >
                Managed
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={deskMode === "candidates"}
                className={deskMode === "candidates" ? "is-on" : ""}
                onClick={() => onDeskModeChange?.("candidates")}
              >
                Candidates
              </button>
            </div>
            <div
              ref={searchWrapRef}
              className={`am-chrome-search am-chrome-search-expand ${
                isExpanded ? "am-chrome-search-open" : ""
              } ${hasQuery ? "am-chrome-search-active" : ""}`}
            >
              {!isExpanded ? (
                <button
                  type="button"
                  className="am-chrome-search-toggle"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Search leagues"
                >
                  <Search size={14} strokeWidth={2.3} aria-hidden />
                </button>
              ) : (
                <>
                  <Search size={14} aria-hidden className="am-chrome-search-icon" />
                  <input
                    ref={inputRef}
                    type="search"
                    placeholder="League name or API id…"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label="Search leagues"
                  />
                  {hasQuery ? (
                    <button type="button" onClick={() => onSearchChange("")} className="am-chrome-search-clear" aria-label="Clear search">
                      <X size={12} />
                    </button>
                  ) : (
                    <button type="button" onClick={closeSearch} className="am-chrome-search-clear" aria-label="Close search">
                      <X size={12} />
                    </button>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              className={`am-chrome-btn am-chrome-btn-filters lg:hidden ${
                filtersOpen || activeFilterCount ? "am-chrome-btn-on" : ""
              }`}
              onClick={onFiltersToggle}
              aria-label="Filters"
            >
              <SlidersHorizontal size={15} strokeWidth={2.2} />
              {activeFilterCount ? <span className="am-chrome-badge">{activeFilterCount}</span> : null}
            </button>

            <button type="button" className="am-chrome-btn am-chrome-btn-on" onClick={onAdd} title="Add league">
              <Plus size={14} strokeWidth={2.3} />
              <span className="hidden sm:inline">Add</span>
            </button>

            {deskMode === "candidates" ? (
              <button
                type="button"
                className="am-chrome-btn am-chrome-btn-accent"
                onClick={onDiscover}
                disabled={discovering || loading}
                title="Discover up to 20 new active leagues from API-Sports"
              >
                {discovering ? <Loader2 size={14} className="am-spin" /> : <Radar size={14} strokeWidth={2.3} />}
                <span className="hidden sm:inline">{discovering ? "Discovering…" : "Discover 20"}</span>
              </button>
            ) : null}

            <button
              type="button"
              className="am-chrome-btn am-chrome-btn-icon"
              onClick={onRefresh}
              disabled={loading}
              aria-label="Refresh"
            >
              {loading ? <Loader2 size={14} className="am-spin" /> : <RefreshCw size={14} strokeWidth={2.3} />}
            </button>
          </div>
        </div>
      </header>
      <div className="am-chrome-spacer" aria-hidden />
    </>
  );
}
